import {toZonedTime} from 'date-fns-tz';

class AutomationEngine {
    static KEYS = {
        SESSION_START: '__liveAssist_session_start',
        SESSION_PAGEVIEWS: '__liveAssist_session_pageviews',
        VISITS_TOTAL: '__liveAssist_visits_len',
        TAB_VISITED_FLAG: '__liveAssist_this_tab_visited',
        CLOSE_CHAT: '__liveAssist_close_chat_ts'
    };

    constructor(deps = {}) {
        this.deps = {
            getOperatorOnline: () => false,
            getVisitor: () => ({cityId: null, countryId: null}),
            onAction: (_action) => {
            },
            stopAfterFirstAction: true,

            tzOffsetMinutes: 0,
            storagePrefix: '',
            now: () => Date.now(),
            // log: (...a) => console.log('[automation]', ...a),
            log: null,
            ...deps
        };

        this.triggers = [];
        this._running = false;

        this._hostExpected = false;
        this._hostReady = false;

        this._pageStart = this._now();
        this._sessionStart = this._getOrInit(
            AutomationEngine.KEYS.SESSION_START,
            'sessionStorage',
            this._now()
        );

        this._pageViews = this._getOrInit(
            AutomationEngine.KEYS.SESSION_PAGEVIEWS,
            'sessionStorage',
            1
        );

        this._visitsLen = this._touchVisitsOncePerTab();
        this._scrollPct = 0;

        this._times = {closeChat: null, firstMessage: null, lastMessage: null};
        const storedClose = Number(this._get(AutomationEngine.KEYS.CLOSE_CHAT, 'localStorage'));
        if (Number.isFinite(storedClose) && storedClose > 0) {
            this._times.closeChat = storedClose;
        }
        this._firedInPage = new Set();
        this._tick = null;

        this._host = {
            title: null,
            url: null,
            scrollPct: null,
            pageViewIndex: null,
            visitsLength: null,
            origin: null
        };

        this._scrollHandler = null;
        this._scrollRoot = null;
        this._detachHostBridge = null;

        this._storageCache = new Map();
        this._storageRequests = new Map();
        this._storageRequestSeq = 0;
        this._hostStorageAvailable = this._supportsHostStorage();
        this._storageReady = !this._hostStorageAvailable;
        this._storageLoading = false;
        this._storageResponseHandler = this._handleStorageResponse.bind(this);
        this._storageListenerAttached = false;
        this._attachStorageListener();
    }

    _attachStorageListener() {
        if (!this._hostStorageAvailable) return;
        if (this._storageListenerAttached) return;
        if (typeof window === 'undefined') return;

        window.addEventListener('message', this._storageResponseHandler);
        this._storageListenerAttached = true;
    }

    setTriggers(arr) {
        this.triggers = Array.isArray(arr) ? arr.map(this._normalizeTrigger) : [];
        if (this._hostStorageAvailable) {
            this._storageReady = false;
            this._storageLoading = true;
            this._loadStorageForTriggers();
        } else {
            this._storageCache.clear();
            this._storageReady = true;
        }
        this._evaluateAll('setTriggers');
    }

    start() {
        if (this._running) return;
        this._running = true;
        this._bindScroll();
        this._tick = setInterval(() => this._evaluateAll('tick'), 1000);
        this._evaluateAll('start');
    }

    stop() {
        this._running = false;
        if (this._scrollHandler && this._scrollRoot) {
            try {
                this._scrollRoot.removeEventListener('scroll', this._scrollHandler);
            } catch {
            }
        }
        if (this._tick) clearInterval(this._tick);
        this._tick = null;
    }

    notifyUrlOrTitleChanged() {
        this.bumpInternalPageview();
        this._evaluateAll('route');
    }

    evaluateRoute() {
        this._evaluateAll('route');
    }

    notifyChatClosed() {
        const ts = this._now();
        this._times.closeChat = ts;
        this._set(AutomationEngine.KEYS.CLOSE_CHAT, ts, 'localStorage');
        this._evaluateAll('close');
    }

    notifyFirstMessage() {
        const ts = arguments.length ? Number(arguments[0]) : NaN;
        if (!this._times.firstMessage) {
            this._times.firstMessage = (Number.isFinite(ts) && ts > 0) ? ts : this._now();
        }
        this._evaluateAll('first');
    }

    notifyLastMessage() {
        const ts = arguments.length ? Number(arguments[0]) : NaN;
        this._times.lastMessage = (Number.isFinite(ts) && ts > 0) ? ts : this._now();
        this._evaluateAll('last');
    }

    notifyOperatorStatusChanged() {
        this._evaluateAll('op');
    }

    bumpInternalPageview() {
        this._pageViews = this._inc(AutomationEngine.KEYS.SESSION_PAGEVIEWS, 'sessionStorage');
        this._evaluateAll('bump');
    }

    attachHostBridge(allowedOrigins = []) {
        this._attachStorageListener();

        const allow = (origin) => {
            if (!allowedOrigins?.length) return true;
            const norm = (s) => String(s || '').replace(/\/+$/, '');
            const hostOnly = (s) => {
                try {
                    return new URL(s).origin;
                } catch {
                    return s;
                }
            };
            return allowedOrigins.some((a) => norm(hostOnly(a)) === norm(origin));
        };

        const onMsg = (ev) => {
            try {
                if (!allow(ev.origin)) return;
                const d = ev.data;
                if (!d || (d.type !== 'stg:host-metrics' && d.type !== 'liveAssist:host-metrics')) return;
                const p = d.payload || {};

                this._host = {
                    title: typeof p.title === 'string' ? p.title : this._host.title,
                    url: typeof p.url === 'string' ? p.url : this._host.url,
                    scrollPct: Number.isFinite(p.scrollPct) ? p.scrollPct : this._host.scrollPct,
                    pageViewIndex: Number.isFinite(p.pageViewIndex) ? p.pageViewIndex : this._host.pageViewIndex,
                    visitsLength: Number.isFinite(p.visitsLength) ? p.visitsLength : this._host.visitsLength,
                    origin: ev.origin
                };

                const wasReady = this._hostReady;
                this._hostReady = true;

                this._evaluateAll('host-metrics');
                if (!wasReady) this._evaluateAll('host-metrics:first');
            } catch (e) {
                console.warn('[automation] bridge error', e);
            }
        };

        window.addEventListener('message', onMsg);
        this._hostExpected = true;

        const ping = () => {
            try {
                window.parent?.postMessage({type: 'stg:widget:handshake'}, '*');
            } catch {
            }
            try {
                window.parent?.postMessage({type: 'liveAssist:widget:handshake'}, '*');
            } catch {
            }
        };
        ping();

        let tries = 0;
        const hiTimer = setInterval(() => {
            if (tries++ >= 5) return clearInterval(hiTimer);
            ping();
        }, 500);

        this._detachHostBridge = () => {
            window.removeEventListener('message', onMsg);
            try {
                clearInterval(hiTimer);
            } catch {
            }
        };
    }

    detachHostBridge() {
        this._detachHostBridge?.();
        this._detachHostBridge = null;
    }

    _now() {
        return Number(this.deps.now?.()) || Date.now();
    }

    _getZonedDate(ts = this._now()) {
        const timezone = typeof this.deps.timezone === 'string' ? this.deps.timezone.trim() : '';
        if (timezone) {
            try {
                return toZonedTime(ts, timezone);
            } catch {
                // fall back to manual offset if timezone lookup fails
            }
        }
        const off = Number(this.deps.tzOffsetMinutes) || 0;
        if (off) {
            return new Date(ts + off * 60 * 1000);
        }
        return new Date(ts);
    }

    _supportsHostStorage() {
        return typeof window !== 'undefined' && window.parent && window.parent !== window;
    }

    _handleStorageResponse(ev) {
        const data = ev.data || {};
        if (!data || data.type !== 'liveAssist:host-storage-response' || !data.requestId) return;
        const pending = this._storageRequests.get(data.requestId);
        if (!pending) return;
        clearTimeout(pending.timer);
        this._storageRequests.delete(data.requestId);
        if (data.error) {
            pending.reject(new Error(data.error));
        } else {
            pending.resolve(data.values ?? data.value ?? null);
        }
    }

    _collectStorageKeys() {
        const keys = new Set();
        for (const trig of this.triggers) {
            const onceC = trig.conditions?.find((c) => c.type === 'once');
            if (onceC) {
                const {primaryKey, fallbackKey} = this._getOnceKeyInfo(onceC, trig);
                if (primaryKey) keys.add(primaryKey);
                if (fallbackKey) keys.add(fallbackKey);
            }
            if (trig.cooldownSec) {
                keys.add(this._cooldownKey(trig));
            }
        }
        return keys;
    }

    async _loadStorageForTriggers() {
        if (!this._hostStorageAvailable) {
            this._storageReady = true;
            this._storageLoading = false;
            return;
        }

        const keys = Array.from(this._collectStorageKeys());
        this._storageCache.clear();

        if (!keys.length) {
            this._storageLoading = false;
            this._storageReady = true;
            this._evaluateAll('storage-ready');
            return;
        }

        this._storageLoading = true;
        this._storageReady = false;

        let loadError;
        try {
            const result = await this._sendStorageRequest('bulk-get', {keys});
            if (result && typeof result === 'object') {
                Object.entries(result).forEach(([k, v]) => {
                    if (v != null) this._storageCache.set(k, String(v));
                });
            }
        } catch (err) {
            loadError = err;
            this._handleStorageFailure(err);
        }

        if (!this._hostStorageAvailable) {
            this._storageReady = true;
            this._storageLoading = false;
            this._evaluateAll('storage-ready');
            if (loadError) {
                console.warn('[automation] host storage unavailable', loadError);
            }
            return;
        }

        for (const trig of this.triggers) {
            const onceC = trig.conditions?.find((c) => c.type === 'once');
            if (onceC) {
                const {primaryKey, fallbackKey} = this._getOnceKeyInfo(onceC, trig);
                if (!this._storageCache.has(primaryKey) && this._storageCache.has(fallbackKey)) {
                    this._storageCache.set(primaryKey, this._storageCache.get(fallbackKey));
                }
            }
        }

        this._storageLoading = false;
        this._storageReady = true;
        this._evaluateAll('storage-ready');
    }

    _handleStorageFailure(err) {
        if (!this._hostStorageAvailable) return;
        this._hostStorageAvailable = false;
        this._storageReady = true;
        this._storageLoading = false;
        this._storageRequests.forEach((pending) => {
            clearTimeout(pending.timer);
            pending.reject(err || new Error('host-storage-failure'));
        });
        this._storageRequests.clear();
        console.warn('[automation] host storage disabled', err);
    }

    _sendStorageRequest(action, payload = {}) {
        return new Promise((resolve, reject) => {
            if (!this._hostStorageAvailable) {
                reject(new Error('host-storage-unavailable'));
                return;
            }

            if (typeof window === 'undefined' || !window.parent || window.parent === window) {
                reject(new Error('host-storage-unavailable'));
                return;
            }

            const requestId = `la_${++this._storageRequestSeq}_${Date.now()}`;
            const timer = setTimeout(() => {
                const pending = this._storageRequests.get(requestId);
                if (pending) {
                    this._storageRequests.delete(requestId);
                    pending.reject(new Error('host-storage-timeout'));
                }
            }, 5000);

            this._storageRequests.set(requestId, {resolve, reject, timer});

            try {
                window.parent.postMessage({
                    type: 'liveAssist:storage-request',
                    action,
                    requestId,
                    payload
                }, '*');
            } catch (err) {
                clearTimeout(timer);
                this._storageRequests.delete(requestId);
                reject(err);
            }
        });
    }

    _log(...a) {
        if (this.deps.log) {
            try {
                this.deps.log(...a);
            } catch {
            }
        }
    }

    _storeKey(k) {
        const prefix = String(this.deps.storagePrefix || '').trim();
        if (!prefix) return String(k ?? '');
        const key = String(k ?? '');
        if (key.startsWith(`${prefix}:`)) return key;
        return `${prefix}:${key}`;
    }

    _getOrInit(key, store = 'sessionStorage', def = null) {
        try {
            const s = (store === 'localStorage' ? localStorage : sessionStorage);
            const K = this._storeKey(key);
            const v = s.getItem(K);
            if (v != null) return Number(v) || def;
            s.setItem(K, String(def));
            return def;
        } catch {
            return def;
        }
    }

    _get(key, store = 'localStorage') {
        if (store === 'localStorage' && this._hostStorageAvailable) {
            const K = this._storeKey(key);
            return this._storageCache.has(K) ? this._storageCache.get(K) : null;
        }
        try {
            const s = (store === 'localStorage' ? localStorage : sessionStorage);
            const K = this._storeKey(key);
            const v = s.getItem(K);
            return v == null ? null : v;
        } catch {
            return null;
        }
    }

    _set(key, val, store = 'localStorage') {
        if (store === 'localStorage') {
            const K = this._storeKey(key);
            const str = String(val);
            if (this._hostStorageAvailable) {
                this._storageCache.set(K, str);
                this._sendStorageRequest('set', {key: K, value: str}).catch((err) => {
                    this._handleStorageFailure(err);
                });
                return;
            }
        }
        try {
            const s = (store === 'localStorage' ? localStorage : sessionStorage);
            const K = this._storeKey(key);
            s.setItem(K, String(val));
        } catch {
        }
    }

    _inc(key, store = 'sessionStorage') {
        try {
            const s = (store === 'localStorage' ? localStorage : sessionStorage);
            const K = this._storeKey(key);
            const cur = Number(s.getItem(K) || '0') + 1;
            s.setItem(K, String(cur));
            return cur;
        } catch {
            return 1;
        }
    }

    _dayId(ts = this._now()) {
        try {
            const zoned = this._getZonedDate(ts);
            const dayStart = Date.UTC(zoned.getFullYear(), zoned.getMonth(), zoned.getDate());
            return Math.floor(dayStart / 86400000);
        } catch {
            const off = Number(this.deps.tzOffsetMinutes) || 0;
            const shifted = ts + off * 60 * 1000;
            return Math.floor(shifted / 86400000);
        }
    }

    _touchVisitsOncePerTab() {
        try {
            if (sessionStorage.getItem(AutomationEngine.KEYS.TAB_VISITED_FLAG)) {
                return Number(this._get(AutomationEngine.KEYS.VISITS_TOTAL, 'localStorage') || '1');
            }
            sessionStorage.setItem(AutomationEngine.KEYS.TAB_VISITED_FLAG, '1');
            const cur = Number(this._get(AutomationEngine.KEYS.VISITS_TOTAL, 'localStorage') || '0') + 1;
            this._set(AutomationEngine.KEYS.VISITS_TOTAL, cur, 'localStorage');
            return cur;
        } catch {
            return 1;
        }
    }

    _getScrollRoot() {
        const el = this.deps.getScrollRoot?.();
        if (el && typeof el.scrollTop === 'number') return el;
        return document.scrollingElement || document.documentElement || document.body;
    }

    _bindScroll() {
        this._scrollRoot = this._getScrollRoot();
        this._scrollHandler = () => {
            const el = this._getScrollRoot();
            if (el !== this._scrollRoot) {
                try {
                    this._scrollRoot.removeEventListener('scroll', this._scrollHandler);
                } catch {
                }
                this._scrollRoot = el;
                try {
                    this._scrollRoot.addEventListener('scroll', this._scrollHandler, {passive: true});
                } catch {
                }
            }
            const st = el.scrollTop || 0;
            const sh = el.scrollHeight || 1;
            const ch = el.clientHeight || window.innerHeight || 1;
            const max = Math.max(1, sh - ch);
            const pct = Math.min(100, Math.max(0, Math.round((st / max) * 100)));
            if (pct !== this._scrollPct) {
                this._scrollPct = pct;
                this._evaluateAll('scroll');
            }
        };
        try {
            this._scrollRoot.addEventListener('scroll', this._scrollHandler, {passive: true});
        } catch {
        }
        this._scrollHandler();
    }

    _parseTimeValue(raw) {
        if (raw == null) return {};
        const s = String(raw).trim();
        if (!s) return {};

        const parts = s.split(':');
        if (parts.length > 1) {
            const hour = Number(parts[0]);
            const minute = Number(parts[1]);
            if (!Number.isFinite(hour) || !Number.isFinite(minute)) return {};
            const h = Math.max(0, Math.min(23, Math.floor(hour)));
            const m = Math.max(0, Math.min(59, Math.floor(minute)));
            return {
                valueHour: h,
                valueMinutes: h * 60 + m
            };
        }

        const hour = Number(s);
        if (!Number.isFinite(hour)) return {};
        return {valueHour: Math.max(0, Math.min(23, Math.floor(hour)))};
    }

    _normalizeTrigger = (t) => {
        const truthy = (v) => {
            if (typeof v === 'boolean') return v;
            const s = String(v ?? '').trim().toLowerCase();
            return ['true', '1', 'yes', 'y', 'да', 'есть', 'on'].includes(s);
        };
        const normC = (c) => {
            const type = c.type;
            if (type === 'operatorOnline') {
                const rawEnable = typeof c.enable === 'undefined' ? c.value : c.enable;
                return {type, logic: c.logic || '=', enable: truthy(rawEnable)};
            }
            if (type === 'timeHour') {
                const pickNum = (v) => {
                    const n = Number(v);
                    return Number.isFinite(n) ? n : null;
                };

                let valueMinutes = pickNum(c.valueMinutes ?? c.value_minutes ?? c.minutes ?? c.valueMinute ?? c.value_minute);
                let valueHour = pickNum(c.valueHour ?? c.value_hour ?? c.hour ?? c.valueH ?? c.value_h);

                if (valueMinutes != null) {
                    valueMinutes = Math.max(0, Math.min(23 * 60 + 59, Math.floor(valueMinutes)));
                    valueHour = Math.floor(valueMinutes / 60);
                } else if (valueHour != null) {
                    valueHour = Math.max(0, Math.min(23, Math.floor(valueHour)));
                } else if (c.value && typeof c.value === 'object') {
                    const h = pickNum(c.value.hour ?? c.value.h ?? c.value.valueHour);
                    const m = pickNum(c.value.minute ?? c.value.min ?? c.value.m ?? c.value.valueMinute);
                    if (h != null && m != null) {
                        const hh = Math.max(0, Math.min(23, Math.floor(h)));
                        const mm = Math.max(0, Math.min(59, Math.floor(m)));
                        valueHour = hh;
                        valueMinutes = hh * 60 + mm;
                    } else if (h != null) {
                        valueHour = Math.max(0, Math.min(23, Math.floor(h)));
                    }
                } else {
                    const parsed = this._parseTimeValue(c.value);
                    valueHour = parsed.valueHour;
                    valueMinutes = parsed.valueMinutes;
                }

                return {
                    type,
                    logic: c.logic || '=',
                    valueHour,
                    valueMinutes
                };
            }
            if (type === 'once') {
                const hasMode = typeof c.mode === 'string' && c.mode.length;
                const hasHours = Number.isFinite(Number(c.hours));
                const mode = hasMode
                    ? (c.mode === 'rolling' ? 'rolling' : 'calendar')
                    : (hasHours ? 'rolling' : 'rolling');
                return {
                    type,
                    mode,
                    hours: Number(c.hours) || 24
                };
            }
            if (type === 'city' || type === 'country') {
                const ids = Array.isArray(c.values) ? c.values.map(Number).filter(Number.isFinite) : [];
                return {type, logic: c.logic || '=', values: [...new Set(ids)]};
            }
            if (type === 'pageTitle' || type === 'pageUrl') {
                return {type, logic: c.logic || '=', value: String(c.value ?? '')};
            }
            const n = Number(c.value);
            return {type, logic: c.logic || '=', value: Number.isFinite(n) ? n : null};
        };
        return {
            id: t.id ?? null,
            type: t.type,
            conditionType: t.conditionType === 'any' ? 'any' : 'all',
            systemMessage: t.systemMessage ?? null,
            conditions: Array.isArray(t.conditions) ? t.conditions.map(normC) : [],
            stopAfter: t.stopAfter === true,
            cooldownSec: Number(t.cooldownSec) || 0
        };
    };

    _cmpStr(logic = '=', actual = '', expected = '', {isUrl = false} = {}) {
        const norm = (s) => String(s || '').trim().toLowerCase();

        if (!isUrl) {
            const a = norm(actual), b = norm(expected);
            switch (logic) {
                case '=':
                    return a === b;
                case '≠':
                    return a !== b;
                case '+':
                    return a.includes(b);
                case '-':
                    return !a.includes(b);
                case '^':
                    return a.startsWith(b);
                case '$':
                    return a.endsWith(b);
                default:
                    return a === b;
            }
        }

        const stripSlash = (s) => {
            const v = norm(s);
            if (!v) return v;
            if (v === '/') return v;
            return v.endsWith('/') ? v.slice(0, -1) : v;
        };

        const makeVariants = (raw, withUrl = false) => {
            const base = [];
            if (withUrl) {
                const u = new URL(raw);
                base.push(norm(raw), norm(u.origin + u.pathname), norm(u.pathname));
            } else {
                base.push(norm(raw));
            }
            const out = [];
            for (const v of base) {
                if (!v) continue;
                out.push(v);
                const s = stripSlash(v);
                if (s && s !== v) out.push(s);
            }
            return out.filter((v, i, arr) => arr.indexOf(v) === i);
        };

        try {
            const variants = makeVariants(actual, true);
            const expectedVariants = makeVariants(expected, false);
            const evaluate = (a, b) => {
                switch (logic) {
                    case '=':
                        return a === b;
                    case '≠':
                        return a !== b;
                    case '+':
                        return a.includes(b);
                    case '-':
                        return !a.includes(b);
                    case '^':
                        return a.startsWith(b);
                    case '$':
                        return a.endsWith(b);
                    default:
                        return a === b;
                }
            };
            if (logic === '-' || logic === '≠') {
                return variants.every((a) => expectedVariants.every((b) => evaluate(a, b)));
            }
            return variants.some((a) => expectedVariants.some((b) => evaluate(a, b)));
        } catch {
            // If URL parsing fails, fall back to plain string matching semantics
            const variants = makeVariants(actual, false);
            const expectedVariants = makeVariants(expected, false);
            const evaluate = (aa, bb) => {
                switch (logic) {
                    case '=':
                        return aa === bb;
                    case '≠':
                        return aa !== bb;
                    case '+':
                        return aa.includes(bb);
                    case '-':
                        return !aa.includes(bb);
                    case '^':
                        return aa.startsWith(bb);
                    case '$':
                        return aa.endsWith(bb);
                    default:
                        return aa === bb;
                }
            };
            if (logic === '-' || logic === '≠') {
                return variants.every((a) => expectedVariants.every((b) => evaluate(a, b)));
            }
            return variants.some((a) => expectedVariants.some((b) => evaluate(a, b)));
        }
    }

    _cmpNum(logic = '=', a, b) {
        if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
        switch (logic) {
            case '=':
                return a === b;
            case '≠':
                return a !== b;
            case '>':
                return a > b;
            case '≥':
                return a >= b;
            case '<':
                return a < b;
            case '≤':
                return a <= b;
            default:
                return a === b;
        }
    }

    _cmpTimeHour(c, M) {
        const actualHour = Number.isFinite(M.timeHour) ? M.timeHour : null;
        const actualMinutes = Number.isFinite(M.timeMinutes)
            ? M.timeMinutes
            : (actualHour != null && Number.isFinite(M.timeMinute)
                ? actualHour * 60 + M.timeMinute
                : null);

        if (Number.isFinite(c.valueMinutes) && actualMinutes != null) {
            return this._cmpNum(c.logic, actualMinutes, c.valueMinutes);
        }
        if (Number.isFinite(c.valueHour) && actualHour != null) {
            return this._cmpNum(c.logic, actualHour, c.valueHour);
        }
        return false;
    }

    _cmpList(logic = '=', arr = [], id) {
        if (id == null) return false;
        const has = arr.includes(Number(id));
        return logic === '≠' ? !has : has;
    }

    _metrics() {
        const now = this._now();
        const onSite = Math.floor((now - this._sessionStart) / 1000);
        const onPage = Math.floor((now - this._pageStart) / 1000);
        const from = (ts) => (ts ? Math.floor((now - ts) / 1000) : null);
        const zonedDate = this._getZonedDate(now);
        const jsDay = zonedDate.getDay();
        const weekDay = jsDay === 0 ? 7 : jsDay;
        const timeHour = zonedDate.getHours();
        const timeMinute = zonedDate.getMinutes();

        const visitor = this.deps.getVisitor() || {};

        const pageViewIndexInternal = Math.max(1, Math.floor(Number(this._pageViews) || 1));

        const isIframe = typeof window !== 'undefined' && window.parent && window.parent !== window;
        const title = this._host.title ?? (isIframe ? '' : (document.title || ''));
        const url = this._host.url ?? (isIframe ? (document.referrer || '') : window.location.href);

        const pageViewIndex = Math.max(
            1,
            Math.floor(Number.isFinite(this._host.pageViewIndex) ? this._host.pageViewIndex : pageViewIndexInternal)
        );

        const visitsLength = Math.max(
            1,
            Math.floor(Number.isFinite(this._host.visitsLength) ? this._host.visitsLength : this._visitsLen)
        );

        const viewedPageCount = pageViewIndex;

        const scrollPosition = Number.isFinite(this._host.scrollPct) ? this._host.scrollPct : this._scrollPct;

        return {
            operatorOnline: !!this.deps.getOperatorOnline(),
            cityId: visitor.cityId ?? null,
            countryId: visitor.countryId ?? null,

            title,
            url,

            pageViewIndex,
            viewedPageCount,

            timeOnPage: onPage,
            timeOnSite: onSite,
            timeFromCloseChat: from(this._times.closeChat),
            timeFromFirstMessage: from(this._times.firstMessage),
            timeFromLastMessage: from(this._times.lastMessage),
            visitsLength,
            scrollPosition,
            weekDay,
            timeHour,
            timeMinute,
            timeMinutes: (timeHour * 60) + timeMinute
        };
    }

    _evaluateAll(reason) {
        if (this._hostStorageAvailable && !this._storageReady && reason !== 'storage-ready') {
            return;
        }

        const M = this._metrics();
        this._log('reason=', reason, {
            hostExpected: this._hostExpected,
            hostReady: this._hostReady,
            pageViewIndex: M.pageViewIndex,
            viewedPageCount: M.viewedPageCount,
            visitsLength: M.visitsLength
        });

        for (const trig of this.triggers) {
            if (this._isInCooldown(trig)) continue;

            const pageKey = this._hash(JSON.stringify({id: trig.id, type: trig.type})) + ':' + this._pageStart;
            if (this._firedInPage.has(pageKey)) continue;

            const ok = this._evalTrigger(trig, M);
            if (!ok) continue;

            this._firedInPage.add(pageKey);
            this._exec(trig, M);

            if (trig.stopAfter || this.deps.stopAfterFirstAction) {
                this.stop();
            }
            break;
        }
    }

    _evalTrigger(tr, M) {
        const {conditions = [], conditionType = 'all'} = tr;
        if (!conditions.length) return true;
        if (conditionType === 'any') return conditions.some((c) => this._evalCond(c, M, tr));
        return conditions.every((c) => this._evalCond(c, M, tr));
    }

    _exec(trig, metrics) {
        this._log('[automation] action:', trig.type, trig.systemMessage || '', '#', trig.id);
        const onceC = trig.conditions?.find((c) => c.type === 'once');

        const finalize = (ok) => {
            if (ok !== false) {
                if (onceC) this._onceHit(onceC, trig);
                this._armCooldown(trig);
            }
        };

        try {
            const res = this.deps.onAction?.({
                type: trig.type,
                systemMessage: trig.systemMessage ?? null,
                triggerId: trig.id ?? null,
                metrics,
                conditions: Array.isArray(trig.conditions) ? trig.conditions : []
            });
            if (res && typeof res.then === 'function') {
                res.then((v) => {
                    const ok = typeof v === 'object' && v ? ('ok' in v ? v.ok : true) : (v ?? true);
                    finalize(ok);
                }).catch(() => finalize(false));
            } else {
                const ok = typeof res === 'object' && res ? ('ok' in res ? res.ok : true) : (res ?? true);
                finalize(ok);
            }
        } catch {
            finalize(false);
        }
    }

    _evalCond(c, M, trig) {
        let t = c.type;

        const usesHostMetric = (name) =>
            name === 'pageViewIndex' ||
            name === 'viewedPageCount' ||
            name === 'visitsLength' ||
            name === 'viewedTransitions' ||
            name === 'pageTitle' ||
            name === 'pageUrl' ||
            name === 'scrollPosition';

        const waitName = (t === 'viewedTransitions') ? 'pageViewIndex' : t;

        if (this._hostExpected && !this._hostReady) {
            if (usesHostMetric(waitName)) {
                this._log('host-wait', {type: t, waiting: true});
                return false;
            }
        }

        const alias = {
            pageViews: 'pageViewIndex',
            views: 'pageViewIndex',
            visits: 'visitsLength',
            timeOfDay: 'timeHour',
            dayTime: 'timeHour',
            timeFromLastActiveMessage: 'timeFromLastMessage',
            timeFromLastActiveMsg: 'timeFromLastMessage',
            timeFromLastMsg: 'timeFromLastMessage',
            timeFromActiveMessage: 'timeFromLastMessage'
        };
        if (alias[t]) t = alias[t];

        if (t === 'viewedTransitions' || c.type === 'viewedTransitions') {
            const pageIndex = Math.max(1, Math.floor(M.pageViewIndex || 1));
            const transitions = Math.max(0, pageIndex - 1);
            return this._cmpNum(c.logic, transitions, Number.isFinite(c.value) ? c.value : NaN);
        }

        if (t === 'timeHour') return this._cmpTimeHour(c, M);

        if (t === 'operatorOnline') {
            const comparison = (!!c.enable) === M.operatorOnline;
            return c.logic === '≠' ? !comparison : comparison;
        }
        if (t === 'once') return this._onceCheck(c, trig);

        if (t === 'city') return this._cmpList(c.logic, c.values, M.cityId);
        if (t === 'country') return this._cmpList(c.logic, c.values, M.countryId);

        if (t === 'pageTitle') {
            if (!M.title) return false;
            return this._cmpStr(c.logic, M.title, c.value);
        }
        if (t === 'pageUrl') {
            if (!M.url) return false;
            return this._cmpStr(c.logic, M.url, c.value, {isUrl: true});
        }

        const numeric = {
            weekDay: M.weekDay,
            pageViewIndex: M.pageViewIndex,
            viewedPageCount: M.viewedPageCount,
            visitsLength: M.visitsLength,
            scrollPosition: M.scrollPosition
        };
        if (t in numeric) return this._cmpNum(c.logic, numeric[t], Number.isFinite(c.value) ? c.value : NaN);

        const duration = {
            timeOnPage: M.timeOnPage,
            timeOnSite: M.timeOnSite,
            timeFromCloseChat: M.timeFromCloseChat,
            timeFromFirstMessage: M.timeFromFirstMessage,
            timeFromLastMessage: M.timeFromLastMessage
        };
        if (t in duration) return this._cmpNum(c.logic, duration[t], Number.isFinite(c.value) ? c.value : NaN);

        return false;
    }

    _getOnceKeyInfo(c, trig) {
        if (!c.__onceKeyInfo) {
            const trigType = trig?.type ?? null;
            const base = {c, trigType};
            const fallbackCore = JSON.stringify(base);
            const primaryCore = JSON.stringify({c, trigType, trigId: trig?.id ?? null});
            const fallbackKey = this._storeKey('__liveAssist_once_' + this._hash(fallbackCore));
            const primaryKey = this._storeKey('__liveAssist_once_' + this._hash(primaryCore));
            c.__onceKeyInfo = {primaryKey, fallbackKey};
        }
        return c.__onceKeyInfo;
    }

    _onceKey(c, trig) {
        return this._getOnceKeyInfo(c, trig).primaryKey;
    }

    _onceCheck(c, trig) {
        try {
            const {primaryKey, fallbackKey} = this._getOnceKeyInfo(c, trig);
            let raw = this._get(primaryKey, 'localStorage');
            if (!raw && fallbackKey !== primaryKey) {
                raw = this._get(fallbackKey, 'localStorage');
                if (raw) {
                    this._set(primaryKey, raw, 'localStorage');
                }
            }
            if (!raw) return true;

            const mode = c.mode === 'rolling' ? 'rolling' : 'calendar';
            if (mode === 'calendar') {
                const lastDayId = Number(raw);
                return lastDayId !== this._dayId();
            } else {
                const lastTs = Number(raw);
                const hours = Math.max(1, Number(c.hours) || 24);
                return (this._now() - lastTs) >= hours * 3600 * 1000;
            }
        } catch {
            return true;
        }
    }

    _onceHit(c, trig) {
        try {
            const {primaryKey, fallbackKey} = this._getOnceKeyInfo(c, trig);
            const mode = c.mode === 'rolling' ? 'rolling' : 'calendar';
            const value = mode === 'calendar' ? this._dayId() : this._now();
            this._set(primaryKey, value, 'localStorage');
            if (fallbackKey !== primaryKey) {
                this._set(fallbackKey, value, 'localStorage');
            }
        } catch {
        }
        return true;
    }

    _cooldownKey(trig) {
        const core = JSON.stringify({id: trig?.id ?? null, type: trig?.type ?? null});
        return this._storeKey('__liveAssist_trig_cooldown_' + this._hash(core));
    }

    _isInCooldown(trig) {
        if (!trig.cooldownSec) return false;
        try {
            const raw = this._get(this._cooldownKey(trig), 'localStorage');
            if (!raw) return false;
            const last = Number(raw);
            return (this._now() - last) < trig.cooldownSec * 1000;
        } catch {
            return false;
        }
    }

    _armCooldown(trig) {
        if (!trig.cooldownSec) return;
        try {
            this._set(this._cooldownKey(trig), this._now(), 'localStorage');
        } catch {
        }
    }

    _hash(s) {
        let h = 0, i = 0;
        for (; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
        return Math.abs(h).toString(36);
    }
}

function init(config) {
    const eng = new AutomationEngine(config);
    return {
        setTriggers: (t) => eng.setTriggers(t),
        start: () => eng.start(),
        stop: () => eng.stop(),
        notifyUrlOrTitleChanged: () => eng.notifyUrlOrTitleChanged(),
        evaluateRoute: () => eng.evaluateRoute(),
        notifyChatClosed: () => eng.notifyChatClosed(),
        notifyFirstMessage: (ts) => eng.notifyFirstMessage(ts),
        notifyLastMessage: (ts) => eng.notifyLastMessage(ts),
        notifyOperatorStatusChanged: () => eng.notifyOperatorStatusChanged(),

        update: () => {
            eng.notifyOperatorStatusChanged();
            eng.notifyChatClosed();
            eng.notifyFirstMessage();
            eng.notifyLastMessage();
            eng.notifyOperatorStatusChanged();
        },
        setTzOffsetMinutes: (m) => {
            eng.deps.tzOffsetMinutes = Number(m) || 0;
        },

        attachHostBridge: (allowed) => eng.attachHostBridge(allowed),
        detachHostBridge: () => eng.detachHostBridge(),

        bumpInternalPageview: () => eng.bumpInternalPageview(),

        get deps() {
            return eng.deps;
        },
        set deps(v) {
            eng.deps = {...eng.deps, ...v};
        }
    };
}

export {AutomationEngine, init};
